import React, { useState } from 'react';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { Text, toast } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import { useWorkflowRunModel } from '../workflow-run.model';
import { Separator } from '@cotera/client/v0/components/ui/separator';
import {
  PayloadBlock,
  Section,
  StepError,
  StepHeader,
} from './run-step-view-sections';
import { describeField } from './filter-run-step-view';
import { formatInTimezone } from '../debounce-timezone';
import type { WorkflowRunStepView, WorkflowRunStepViewProps } from './types';

const {
  WorkflowNodeType,
  DEBOUNCE_WINDOW_LABELS,
  parseDebounceConfig,
  resolveDebounceKey,
  computeNextDebounceBucketStart,
} = PersistedWorkflow;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Step view for a debounce node, built around the one question anyone opens it
 * to ask: why didn't this fire?
 *
 * A stopped debounce is a success, not a failure, and the default JSON view
 * cannot say so — it shows identical input and output (the node passes its
 * value through untouched) and a "Stopped" badge that reads like something
 * broke. This names the key, the window, and when that key comes back.
 */
export const DebounceRunStepView: React.FC<WorkflowRunStepViewProps> = ({
  node,
  payload,
  sourceLabels,
}) => {
  const parsed = parseDebounceConfig(isRecord(node.data) ? node.data : {});
  const recordedInput = payload?.input ?? null;
  const upstream = PersistedWorkflow.resolveRecordedStepInput(recordedInput);

  const suppressed = node.status === 'stopped';
  const passed = node.status === 'completed';
  const failed = node.status === 'failed';

  const resolvedKey = parsed.ok
    ? resolveDebounceKey({ upstream, config: parsed.config })
    : null;

  // Derived from *now* rather than from the claim, which the step does not
  // record: it answers "when does this key come back", which is what the
  // reader wants, and for a step being read during its own window the two
  // agree anyway.
  const reopensAt =
    parsed.ok && suppressed
      ? computeNextDebounceBucketStart({
          now: new Date(),
          config: parsed.config,
        })
      : null;

  return (
    <div className="bg-background flex h-full flex-col gap-6 overflow-y-auto px-6 py-6">
      <StepHeader name={node.name} status={node.status} />

      {/* A failed debounce never reached its decision, so the error is the
          answer to "why didn't this fire" — it leads, ahead of the config. */}
      {node.error !== null && node.error !== '' ? (
        <Section title="Error">
          <StepError error={node.error} />
        </Section>
      ) : null}

      <Section title="Debounce">
        {parsed.ok ? (
          <div className="flex flex-col gap-2">
            <Text.Caption className="text-muted-foreground block">
              Lets each {describeField(parsed.config.keyPath, sourceLabels)}{' '}
              through once {DEBOUNCE_WINDOW_LABELS[parsed.config.window]}, on{' '}
              {parsed.config.timezone} time.
            </Text.Caption>
            {resolvedKey !== null && resolvedKey.ok ? (
              <Text.Caption className="text-muted-foreground block">
                Key for this run: <code>{resolvedKey.key}</code>
              </Text.Caption>
            ) : null}
            {resolvedKey !== null && !resolvedKey.ok && failed ? (
              <Text.Caption className="text-muted-foreground block">
                The key could not be read from this step&apos;s input, so the
                node had nothing to debounce on. Check that the step feeding it
                actually carries{' '}
                <code>
                  {describeField(parsed.config.keyPath, sourceLabels)}
                </code>
                .
              </Text.Caption>
            ) : null}
            {passed ? (
              <Text.Caption className="text-muted-foreground block">
                First time this key was seen in the current window, so it
                continued.
              </Text.Caption>
            ) : null}
            {suppressed ? (
              <Text.Caption className="text-muted-foreground block">
                Already let through in this window, so the rest of this branch
                was skipped. This key becomes eligible again on{' '}
                {reopensAt === null
                  ? 'the next window'
                  : formatInTimezone(reopensAt, parsed.config.timezone)}
                .
              </Text.Caption>
            ) : null}
            {suppressed ? (
              <ClearDebounceControl runNodeId={node.nodeId} />
            ) : null}
          </div>
        ) : (
          <Text.Caption className="text-muted-foreground block">
            {parsed.error}
          </Text.Caption>
        )}
      </Section>

      <Separator />

      <Section title="Input">
        <PayloadBlock
          value={recordedInput}
          emptyText="No input recorded for this step."
        />
      </Section>
    </div>
  );
};

/**
 * The override: releases what this step was suppressed on so it fires again.
 *
 * Shown only on a stopped step, because that is the only state with a claim to
 * release. The server resolves both the node and the key from the step, so this
 * sends nothing but the step's own identifiers.
 */
const ClearDebounceControl: React.FC<{ runNodeId: string }> = ({
  runNodeId,
}) => {
  const client = useTenantedClient();
  const model = useWorkflowRunModel();
  const [clearing, setClearing] = useState(false);
  const [releasedKey, setReleasedKey] = useState<string | null>(null);

  const clear = async (): Promise<void> => {
    setClearing(true);
    const result = await client.resource.workflow.clearDebounce({
      workflowId: model.workflowId,
      runId: model.runId,
      nodeId: runNodeId,
    });
    setClearing(false);
    if (result.isErr()) {
      toast.error('Could not let this through');
      return;
    }
    setReleasedKey(result.value.key);
    toast.success(`${result.value.key} will be let through on the next run`);
    model.refresh();
  };

  // Settled state rather than a button that can be pressed again. The step
  // stays Stopped — that is history and does not change — so without this the
  // control looks untouched, and a second press reported "already eligible",
  // which reads as though the first one had not worked.
  //
  // `cleared: 0` lands here too, and correctly: it means the key was eligible
  // already, so "will be let through on the next run" is just as true.
  if (releasedKey !== null) {
    return (
      <Text.Caption className="text-muted-foreground block">
        {releasedKey} was released and will be let through on the next run.
      </Text.Caption>
    );
  }

  return (
    <Button
      variant="outline"
      size="sm"
      className="w-fit"
      disabled={clearing}
      onClick={() => void clear()}
    >
      {clearing ? 'Letting through…' : 'Let this through'}
    </Button>
  );
};

export const debounceRunStepView: WorkflowRunStepView = {
  key: 'debounce',
  matches: (nodeType) => nodeType === WorkflowNodeType.Debounce,
  needsPayload: true,
  render: (props) => <DebounceRunStepView {...props} />,
};
